import { useEffect } from 'react';
import { SITE_NAME, DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE, absUrl } from './config';

/**
 * Dependency-free document-head manager for the public marketing site. Renders
 * nothing; on mount/update it upserts title, description, canonical, robots and
 * the Open Graph / Twitter card tags, and injects any per-page JSON-LD (removed
 * again on unmount so structured data never leaks between routes).
 *
 * Google renders JS and picks these up on the client; the site-wide essentials
 * (Organization/WebSite, default title/description/OG) ALSO live statically in
 * index.html so non-JS crawlers and social scrapers get a correct baseline.
 */
export interface SeoProps {
  /** Page title, WITHOUT the brand suffix (e.g. "Products"). Omit on the home page. */
  title?: string;
  description?: string;
  /** Canonical path (defaults to the current pathname). Query strings are dropped. */
  path?: string;
  /** Absolute URL or site-relative path to the social card image. */
  image?: string;
  /** og:type — 'website' (default), 'article', or 'product'. */
  type?: 'website' | 'article' | 'product' | 'profile';
  /** Keep this route out of search (app pages, thin lead forms, token pages). */
  noindex?: boolean;
  /** One JSON-LD object or an array of them. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${CSS.escape(key)}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function Seo({ title, description, path, image, type = 'website', noindex, jsonLd }: SeoProps) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';
  useEffect(() => {
    const fullTitle = title
      ? `${title} · ${SITE_NAME}`
      : `${SITE_NAME} — Bio-fertilizers, Bio-stimulants & Crop-care`;
    const desc = (description ?? DEFAULT_DESCRIPTION).replace(/\s+/g, ' ').trim().slice(0, 300);
    const pathname = (path ?? (typeof window !== 'undefined' ? window.location.pathname : '/')).split('?')[0];
    const url = absUrl(pathname);
    const img = absUrl(image ?? DEFAULT_OG_IMAGE);

    document.title = fullTitle;
    upsertMeta('name', 'description', desc);
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
    upsertMeta('name', 'googlebot', noindex ? 'noindex, nofollow' : 'index, follow');
    upsertLink('canonical', url);

    // Open Graph
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', img);
    upsertMeta('property', 'og:locale', 'en_IN');

    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', desc);
    upsertMeta('name', 'twitter:image', img);

    // Per-page JSON-LD — appended fresh, removed on cleanup.
    const items = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    const scripts = items.map((item) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-jsonld', '');
      s.text = JSON.stringify(item);
      document.head.appendChild(s);
      return s;
    });
    return () => { scripts.forEach((s) => s.remove()); };
  }, [title, description, path, image, type, noindex, jsonLdKey]);

  return null;
}
